import React, { useMemo, useState } from 'react';
import { useBoolean } from 'ahooks';
import useClientPaginationAndOrder, {
  ClientOrderComparatorsBuilders,
} from '@intelligence/hooks/useClientPaginationAndOrder';
import MobileOverlapDetailsService from '../adapter';
import { getCountryFlag } from '@home/_config/countries';
import { handleTableDefaultSelectGames } from '@intelligence/utils/tableUtils';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';
import { TOPGAMENUM } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { handleMetrics } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/utils';
import { DEFAULT_FETCH_PARAMS } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/constants';

import type { IGameType } from '@intelligence/types/common';
import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { RecordItemType } from '@intelligence/components/SearchRecent/type';
import type {
  IMobileDetailsOverlapRanksFetchParams,
  IMobileDetailsOverlapTrendsFetchParams,
  IMobileDetailsOverlapRank,
  IMobileDetailsOverlapTrend,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/types';

interface IUseOverlapProps {
  entityType: IGameType;
  getInitialFetchParams: () => IMobileDetailsOverlapRanksFetchParams;
  refreshListDataCallback?: (payload: IMobileDetailsOverlapRank[]) => void;
}

interface IUseOverlapResponse {
  fetchParams: IMobileDetailsOverlapRanksFetchParams;
  // 宽表总数据条数
  tableTotal: number;
  // 宽表数据
  tableData: IMobileDetailsOverlapRank[];
  // echarts图表类型
  chartType: ChartType;
  // echarts图表类型 setter
  setChartType: React.Dispatch<React.SetStateAction<ChartType>>;
  // 柱状图是否聚合
  isAggregate: boolean;
  // 柱状图是否聚合 setter
  setIsAggregate: (value: boolean) => void;
  // 柱状图、饼图 数据源
  barOrPieData: IMobileDetailsOverlapRank[];
  setBarOrPieData: React.Dispatch<React.SetStateAction<IMobileDetailsOverlapRank[]>>;
  // 折线图数据源
  lineData: IMobileDetailsOverlapTrend[];
  // 首次调用接口后的回调
  getGameData: (params: IMobileDetailsOverlapRanksFetchParams, isCheckOrSearch: boolean) => Promise<void>;
  // 折线图接口后的回调
  getLineChartData: (params: IMobileDetailsOverlapTrendsFetchParams) => Promise<void>;
  // 宽表默认勾选的前6数据
  tableSelection: TableSelectionType[];
  setTableSelection: React.Dispatch<React.SetStateAction<TableSelectionType[]>>;
  // 宽表默认勾选的前6数据
  defaultSelectionGames: TableSelectionType[];
  // 是否重新操作过复选器提交
  isNewComfirm: boolean;
  // 是否重新操作过复选器提交 setter
  setIsNewComfirm: React.Dispatch<React.SetStateAction<boolean>>;
  // 是否重新勾选过游戏
  isNewCheckGame: boolean;
  // 是否重新勾选过游戏 setter
  setIsNewCheckGame: React.Dispatch<React.SetStateAction<boolean>>;
  /** 首次展示下载弹窗提示 */
  firstDownLoadBubleShow: boolean;
  setFirstDownLoadBubleShow: React.Dispatch<React.SetStateAction<boolean>>;
  /** 是否勾选过游戏，取消和全选为没勾选过, 可用于继承和加载可视化Lading  */
  operatedGame: boolean;
  isNodata?: boolean;
  // echarts图表类型 setter
  setOperatedGame: React.Dispatch<React.SetStateAction<boolean>>;
  handleClearSearch: () => void;
  transTableDataForSearch: (payload: any) => RecordItemType[];
  fetchOverlapRanks: (params: IMobileDetailsOverlapRanksFetchParams) => Promise<any>;
  reset: (params?: IMobileDetailsOverlapRanksFetchParams) => void;
}

/**
 * 获取前top的数据
 * @param list 源数据
 * @param end 结束索引
 * @returns 前top的数据
 */
const handleTableToBar = (list: IMobileDetailsOverlapRank[], end = TOPGAMENUM): IMobileDetailsOverlapRank[] => {
  if (!Array.isArray(list)) return [];
  return list.slice(0, end);
};

export default function useOverlap(props: IUseOverlapProps): IUseOverlapResponse {
  const { entityType, getInitialFetchParams, refreshListDataCallback } = props || {};

  const fetchOverlapRanks = useClientPaginationAndOrder(MobileOverlapDetailsService.fetchMobileOverlapDetailsList, {
    // rewriteFields: ['granularity', 'start_date', 'end_date', 'markets'],
    rewriteFields: ['start_date', 'end_date', 'markets'],
    buildComparator: ClientOrderComparatorsBuilders.getDataSourceMetricComparatorBuilder(),
    refreshCallback: refreshListDataCallback,
    handler: (list: IMobileDetailsOverlapRank[]): IMobileDetailsOverlapRank[] => {
      if (!Array.isArray(list)) return [];
      return list.map((item) => {
        return {
          ...item,
          id: item?.unified_id,
          metric_list: handleMetrics(item?.metric_list),
        };
      });
    },
  });

  // 查询表单对象
  const [fetchParams, setFetchParams] = useState<IMobileDetailsOverlapRanksFetchParams>(getInitialFetchParams());

  // 宽表总数据条数
  const [tableTotal, setTableTotal] = useState(0);
  // 宽表数据
  const [tableData, setTableData] = useState<IMobileDetailsOverlapRank[]>([]);
  // echarts图表类型
  const [chartType, setChartType] = useState<ChartType>(ChartType.Line);
  // 柱状图是否聚合
  const [isAggregate, { set: setIsAggregate }] = useBoolean(true);
  // 柱状图、饼图 数据源
  const [barOrPieData, setBarOrPieData] = useState<IMobileDetailsOverlapRank[]>([]);
  // 折线图数据源
  const [lineData, setLineData] = useState<IMobileDetailsOverlapTrend[]>([]);
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);

  // 是否重新操作过复选器提交
  const [isNewComfirm, setIsNewComfirm] = useState(true);
  // 是否重新勾选过游戏
  const [isNewCheckGame, setIsNewCheckGame] = useState(true);
  /** 真正的是否重新勾选游戏， 上面那个isNewCheckGame需要做优化处理 */
  const [operatedGame, setOperatedGame] = useState(false);
  const [firstDownLoadBubleShow, setFirstDownLoadBubleShow] = useState(false);
  const [tableListForClearSearch, setTableListForClearSearch] = useState<IMobileDetailsOverlapRank[]>([]);
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);

  const isNodata = useMemo(() => tableTotal < 1 || tableSelection?.length < 1, [tableTotal, tableSelection]);

  const getGameData = async (params: IMobileDetailsOverlapRanksFetchParams, isCheckOrSearch: boolean) => {
    try {
      setTableData([]);
      setFetchParams(params);
      const { data, req } = await fetchOverlapRanks(params);
      const { list: listTmp, total: totalTmp } = data || {};
      const { granularity, start_date: startDate, end_date: endDate, markets } = req || {};
      const fetchParamsTmp = {
        ...params,
        markets: markets || params?.markets || DEFAULT_FETCH_PARAMS.markets,
        granularity: granularity || params?.granularity || DEFAULT_FETCH_PARAMS.granularity,
        start_date: startDate || params?.start_date || DEFAULT_FETCH_PARAMS.start_date,
        end_date: endDate || params?.end_date || DEFAULT_FETCH_PARAMS.end_date,
      };
      const tableDataTmp = listTmp?.map((item: any) => ({ ...item, id: item?.unified_id })) || [];
      const total = totalTmp || tableDataTmp?.length || 0;
      if (params?.page === 1) {
        /** 第一页时拿到默认前6个游戏作为游戏全选值 */
        const defaultGames = handleTableDefaultSelectGames(tableDataTmp?.slice(0, TOPGAMENUM), 'id');
        setDefaultSelectionGames(defaultGames);
        /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
        if (!isCheckOrSearch) {
          setTableSelection(defaultGames);
          /** 分页排序请求接口时，可视化不改变 */
          const barOrPieDataTmp = handleTableToBar(tableDataTmp, TOPGAMENUM);
          setBarOrPieData(barOrPieDataTmp);
          if (chartType === ChartType.Line || (chartType === ChartType.Bar && !isAggregate)) {
            const defaultGamesIds = defaultGames?.map((item) => item?.unified_id) || [];
            await getLineChartData({ ...fetchParamsTmp, cross_app_ids: defaultGamesIds });
          }
        }
      }
      if (!params?.search_by_ids?.length) {
        // 有game_code表示是搜索，则不存页面数据
        setTableListForClearSearch(tableDataTmp);
        setTotalForClearSearch(total);
      }
      setFetchParams(fetchParamsTmp);
      setTableTotal(total);
      setTableData(tableDataTmp);
    } catch (e) {
      setTableData([]);
      setBarOrPieData([]);
    } finally {
    }
  };

  const getLineChartData = async (params: IMobileDetailsOverlapTrendsFetchParams) => {
    const idList = params?.cross_app_ids || [];
    if (idList?.length > 0) {
      try {
        const fetchParamsTmp = { ...params, order_type: params?.order };
        setLineData([]);
        const { data /* barList */ } = await MobileOverlapDetailsService.fetchMobileOverlapDetailsTrends({
          ...fetchParamsTmp,
        });
        const { trends: metricTimelineList } = data || {};
        const metricTimelineListTmp = metricTimelineList?.map((item: any) => {
          const trendMetricListTmp = item?.trend_metric_list?.map((subItem: any) => {
            return {
              ...subItem,
              metric_value_list: handleMetrics(subItem?.metric_value_list),
            };
          });
          return {
            ...item,
            id: item?.unified_id,
            trend_metric_list: trendMetricListTmp || [],
          };
        });
        setLineData(metricTimelineListTmp || []);
        setIsNewComfirm(false);
        setIsNewCheckGame(false);
      } catch (e) {
        console.error(e);
      } finally {
      }
    } else {
      // 未勾选任何游戏，不请求，返回为空数据
      setLineData([]);
    }
  };

  const handleClearSearch = () => {
    setFetchParams({ ...fetchParams, search_by_ids: [] });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const transTableDataForSearch = (res: any): RecordItemType[] => {
    const resultList = res?.data?.list;
    if (!Array.isArray(resultList)) return [];

    return resultList.map((item) => {
      const countryCode = item?.publisher_addr;
      return {
        id: item?.unified_id,
        imageType: entityType,
        imgSrc: item?.cover,
        lineOneStr: item?.name,
        lineTwoImg: getCountryFlag(countryCode),
        lineTwoImgName: countryCode,
        lineTwoStr: item?.publisher,
      };
    });
  };

  const reset = (params?: IMobileDetailsOverlapRanksFetchParams) => {
    setTableTotal(0);
    setTableData([]);
    setBarOrPieData([]);
    setLineData([]);
    setDefaultSelectionGames([]);
    setTableSelection([]);
    setTotalForClearSearch(0);
    setTableListForClearSearch([]);
    setIsNewComfirm(true);
    setIsNewCheckGame(true);
    setOperatedGame(false);
    setFetchParams({ ...fetchParams, ...params });
  };

  return {
    isNodata,
    fetchParams,
    tableTotal,
    tableData,
    chartType,
    setChartType,
    isAggregate,
    setIsAggregate,
    barOrPieData,
    lineData,
    getGameData,
    getLineChartData,
    defaultSelectionGames,
    isNewComfirm,
    setIsNewComfirm,
    isNewCheckGame,
    setIsNewCheckGame,
    tableSelection,
    setBarOrPieData,
    firstDownLoadBubleShow,
    setFirstDownLoadBubleShow,
    operatedGame,
    setOperatedGame,
    setTableSelection,
    handleClearSearch,
    transTableDataForSearch,
    fetchOverlapRanks,
    reset,
  };
}
